import { FC, ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BerthMove,
  bollardToPct,
  getInitialScrollMs,
  msToPx,
  pxToMs,
  QUAY_BOLLARDS,
  stepThrough,
} from '@/components/blocks/berth-plan/berth-plan.utils';
import BerthPlanBlock from '@/components/blocks/berth-plan/berth-plan-block';
import BerthPlanToolbar from '@/components/blocks/berth-plan/berth-plan-toolbar';
import {
  BerthPlanVisit,
  StepDirection,
  VisitEditorOptions,
} from '@/components/blocks/berth-plan/types';
import UnplacedVisitsPopover from '@/components/blocks/berth-plan/unplaced-visits-popover';
import { useBerthPlanView } from '@/components/blocks/berth-plan/use-berth-plan-view';

/** Bollards between quay labels and grid lines. */
const QUAY_TICK_STEP = 10;
const QUAY_TICKS = [
  1,
  ...Array.from(
    { length: Math.floor(QUAY_BOLLARDS / QUAY_TICK_STEP) },
    (_, index) => (index + 1) * QUAY_TICK_STEP
  ),
];
/** Height of the sticky bollard header inside the scroll area (`h-6`). */
const TICK_HEADER_PX = 24;

/** Makes the plan editable: blocks drag, and a click opens `renderEditor`. */
export interface BerthPlanEditing {
  onMoveVisit: (visitId: string, move: BerthMove) => void;
  renderEditor: (visitId: string, options: VisitEditorOptions) => ReactNode;
}

interface BerthPlanProps {
  visits: ReadonlyArray<BerthPlanVisit>;
  cutoffMs?: number;
  selectedVisitId: string | null;
  onSelectVisit: (visitId: string) => void;
  onDeselect: () => void;
  /** Absent for a read-only plan. */
  editing?: BerthPlanEditing;
}

const BerthPlan: FC<BerthPlanProps> = ({
  visits,
  cutoffMs,
  selectedVisitId,
  onSelectVisit,
  onDeselect,
  editing,
}) => {
  const { t, i18n } = useTranslation();
  const view = useBerthPlanView(visits, cutoffMs);
  const { layout, pxPerHour } = view;
  const scrollRef = useRef<HTMLDivElement>(null);
  const quayRef = useRef<HTMLDivElement>(null);
  // Cut-off last scrolled to; null until the first scroll.
  const scrolledCutoffMs = useRef<number | undefined | null>(null);
  // Time at the middle of the view when zooming, kept in place afterwards.
  const zoomCenterMs = useRef<number | null>(null);
  // Offset of the time axis in the scroll area: the header, plus the history
  // button while it shows.
  const getAxisTop = () => quayRef.current?.offsetTop ?? TICK_HEADER_PX;

  useEffect(() => {
    if (!layout || !scrollRef.current) return;
    // Re-scrolls when the cut-off arrives after the first render.
    if (
      scrolledCutoffMs.current !== null &&
      scrolledCutoffMs.current === layout.cutoffMs
    ) {
      return;
    }
    scrolledCutoffMs.current = layout.cutoffMs;
    // Later is lower: keep the focus time in the upper part of the view so
    // the upcoming visits below it are visible.
    const { clientHeight } = scrollRef.current;
    scrollRef.current.scrollTop =
      msToPx(getInitialScrollMs(layout), layout.startMs, pxPerHour) +
      getAxisTop() -
      TICK_HEADER_PX -
      (clientHeight - TICK_HEADER_PX) * 0.25;
  }, [layout, pxPerHour]);

  // Showing or hiding history moves the top of the axis; keep the view still.
  const layoutStartMs = layout?.startMs;
  const prevAxis = useRef<{ startMs: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const prev = prevAxis.current;
    prevAxis.current =
      layoutStartMs === undefined
        ? null
        : { startMs: layoutStartMs, top: getAxisTop() };
    if (!prev || !prevAxis.current || !scrollRef.current) return;
    scrollRef.current.scrollTop +=
      msToPx(prev.startMs, prevAxis.current.startMs, pxPerHour) +
      prevAxis.current.top -
      prev.top;
    // hiddenCount: the history button can come or go on its own after a refetch.
  }, [layoutStartMs, layout?.hiddenCount, pxPerHour]);

  useLayoutEffect(() => {
    const centerMs = zoomCenterMs.current;
    if (centerMs === null || !layout || !scrollRef.current) return;
    zoomCenterMs.current = null;
    scrollRef.current.scrollTop =
      msToPx(centerMs, layout.startMs, pxPerHour) +
      getAxisTop() -
      scrollRef.current.clientHeight / 2;
  }, [pxPerHour, layout]);

  if (!layout) {
    return (
      <p className='text-sm text-muted-foreground'>{t('berthPlan.empty')}</p>
    );
  }

  const { startMs, endMs } = layout;

  const zoom = (change: () => void) => {
    const scroller = scrollRef.current;
    if (scroller) {
      const centerPx =
        scroller.scrollTop + scroller.clientHeight / 2 - getAxisTop();
      zoomCenterMs.current = startMs + pxToMs(centerPx, pxPerHour);
    }
    change();
  };

  const selectStep = (ids: Array<string>, direction: StepDirection) => {
    const nextId = stepThrough(ids, selectedVisitId, direction);
    if (nextId) onSelectVisit(nextId);
  };

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-2'>
      <BerthPlanToolbar
        query={view.query}
        onQueryChange={view.setQuery}
        matchCount={view.matchIds?.size ?? null}
        onFindNext={() => {
          selectStep(view.matchSequence, 1);
        }}
        conflictCount={view.conflictSequence.length}
        conflictPosition={
          selectedVisitId === null
            ? 0
            : view.conflictSequence.indexOf(selectedVisitId) + 1
        }
        onConflictStep={(direction) => {
          selectStep(view.conflictSequence, direction);
        }}
        unplaced={
          <UnplacedVisitsPopover
            visits={layout.unplacedVisits}
            selectedVisitId={selectedVisitId}
            onSelect={onSelectVisit}
            onDeselect={onDeselect}
            renderEditor={
              editing
                ? (visitId): ReactNode =>
                    editing.renderEditor(visitId, {
                      hasConflict: false,
                      canEditBollards: true,
                    })
                : undefined
            }
          />
        }
        canShowHistory={cutoffMs !== undefined}
        showHistory={view.showHistory}
        onShowHistoryChange={view.setShowHistory}
        canZoomIn={view.canZoomIn}
        canZoomOut={view.canZoomOut}
        onZoomIn={() => {
          zoom(view.zoomIn);
        }}
        onZoomOut={() => {
          zoom(view.zoomOut);
        }}
      />
      <ul className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground'>
        {cutoffMs !== undefined && (
          <li className='flex items-center gap-1.5'>
            <span className='size-3 rounded-sm border border-border bg-muted/70' />
            {t('berthPlan.legend.locked')}
          </li>
        )}
        {editing && (
          <li className='flex items-center gap-1.5'>
            <span className='size-3 rounded-sm border border-primary/50 bg-primary/15' />
            {t('berthPlan.legend.editable')}
          </li>
        )}
        <li className='flex items-center gap-1.5'>
          <span className='size-3 rounded-sm border-2 border-destructive bg-destructive/10' />
          {t('berthPlan.conflict')}
        </li>
        {cutoffMs !== undefined && (
          <li className='flex items-center gap-1.5'>
            <span className='w-3.5 border-t-2 border-amber-500' />
            {t('berthPlan.cutoff')}
          </li>
        )}
      </ul>
      <div
        ref={scrollRef}
        className='relative min-h-80 flex-1 overflow-y-auto rounded-md border pl-16 pr-6'>
        <div className='sticky top-0 z-20 h-6 bg-background'>
          {QUAY_TICKS.map((bollard) => (
            <span
              key={bollard}
              className='absolute top-1 translate-x-1/2 text-[10px] text-muted-foreground'
              style={{
                right: `${bollardToPct(bollard + 0.5)}%`,
              }}>
              {bollard}
            </span>
          ))}
        </div>
        {layout.hiddenCount > 0 && (
          <button
            type='button'
            onClick={() => {
              view.setShowHistory(true);
            }}
            className='-ml-16 -mr-6 block w-[calc(100%+5.5rem)] border-b bg-muted/50 py-1.5 text-xs text-muted-foreground hover:bg-muted'>
            {t('berthPlan.history.hidden', {
              count: layout.hiddenCount,
            })}
          </button>
        )}
        <div
          ref={quayRef}
          className='relative'
          style={{ height: msToPx(endMs, startMs, pxPerHour) }}>
          {QUAY_TICKS.map((bollard) => (
            <div
              key={bollard}
              className='pointer-events-none absolute inset-y-0 w-px'
              style={{
                right: `${bollardToPct(bollard + 0.5)}%`,
                backgroundColor: 'var(--border)',
              }}
            />
          ))}
          {layout.dayStarts.map((dayMs) => (
            <div
              key={dayMs}
              className='absolute inset-x-0 border-t-2 border-foreground/20'
              style={{ top: msToPx(dayMs, startMs, pxPerHour) }}>
              <span className='absolute top-0 -left-16 w-14 pt-0.5 text-[11px] leading-tight'>
                {new Date(dayMs).toLocaleDateString(i18n.language, {
                  day: 'numeric',
                  month: 'short',
                })}
                <span className='block text-[10px] uppercase text-muted-foreground'>
                  {new Date(dayMs).toLocaleDateString(i18n.language, {
                    weekday: 'short',
                  })}
                </span>
              </span>
            </div>
          ))}
          {layout.shiftStarts.map((shiftMs) => (
            <div
              key={shiftMs}
              className='absolute inset-x-0 border-t border-foreground/10'
              style={{ top: msToPx(shiftMs, startMs, pxPerHour) }}
            />
          ))}
          {cutoffMs !== undefined && (
            <div
              className='absolute inset-x-0 z-20 border-t-2 border-amber-500'
              style={{ top: msToPx(cutoffMs, startMs, pxPerHour) }}>
              <span className='absolute top-0 right-1 bg-background px-1 text-[10px] text-amber-600'>
                {t('berthPlan.cutoff')}
              </span>
            </div>
          )}
          {layout.blocks.map((block) => {
            const { id } = block.visit;
            const isSelected = id === selectedVisitId;
            const hasConflict = layout.conflictIds.has(id);
            return (
              <BerthPlanBlock
                key={id}
                block={block}
                startMs={startMs}
                pxPerHour={pxPerHour}
                cutoffMs={cutoffMs}
                hasConflict={hasConflict}
                isMatch={view.matchIds?.has(id) ?? false}
                isDimmed={view.matchIds !== null && !view.matchIds.has(id)}
                isSelected={isSelected}
                onSelect={onSelectVisit}
                onDeselect={onDeselect}
                editor={
                  isSelected &&
                  editing?.renderEditor(id, {
                    hasConflict,
                    canEditBollards: false,
                  })
                }
                getPxPerBollard={() =>
                  (quayRef.current?.clientWidth ?? 0) / QUAY_BOLLARDS
                }
                getScrollTop={() => scrollRef.current?.scrollTop ?? 0}
                onMove={editing?.onMoveVisit}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BerthPlan;
